import { useState, type FormEvent } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowRight, MoreHorizontal, Plus, Search } from "lucide-react"
import { toast } from "sonner"
import { ROLE_LABELS, ROLE_SUMMARY, type Invitation, type Member, type Role } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DataTable, Field, Heading, Hint, MiniAvatar, Pick } from "@/components/workspace/primitives"
import { formatDateTime, relativeTime } from "@/lib/format"
import { api, ApiError } from "@/lib/api"
import { teamQuery } from "@/lib/queries"
import { useCan, useMe, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/settings/team")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(teamQuery(params.workspace)),
  component: TeamMembers,
})

const ROLE_OPTIONS = (["agent", "support_manager", "marketing", "admin"] as const).map((value) => ({ value, label: ROLE_LABELS[value] }))
const ROLE_FILTERS = [{ value: "all", label: "All roles" }, ...ROLE_OPTIONS]

type Row =
  | { kind: "member"; key: string; member: Member }
  | { kind: "invitation"; key: string; invitation: Invitation }

function describe(error: unknown) {
  return error instanceof ApiError ? error.problem.title : "Something went wrong"
}

/** Screen 40. */
function TeamMembers() {
  const { workspace } = useWorkspaceContext()
  const me = useMe()
  const can = useCan()
  const canManage = can("manage_team")
  const queryClient = useQueryClient()
  const team = useSuspenseQuery(teamQuery(workspace.slug)).data
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [role, setRole] = useState<Role>("agent")
  const [search, setSearch] = useState("")
  const [filter, setFilter] = useState("all")
  const [roleTarget, setRoleTarget] = useState<Member | null>(null)
  const [nextRole, setNextRole] = useState<Role>("agent")
  const [removeTarget, setRemoveTarget] = useState<Member | null>(null)

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug] })
  const base = `/w/${workspace.slug}`

  const invite = useMutation({
    mutationFn: (input: { email: string; role: Role }) => api<Invitation>(`${base}/invitations`, { method: "POST", body: JSON.stringify(input) }),
    onSuccess: async (invitation) => {
      await refresh()
      setOpen(false)
      setEmail("")
      toast.success(`Invitation sent to ${invitation.email}`)
    },
    onError: (error) => toast.error(describe(error)),
  })
  const resend = useMutation({
    mutationFn: (id: string) => api<Invitation>(`${base}/invitations/${id}/resend`, { method: "POST" }),
    onSuccess: async (invitation) => {
      await refresh()
      toast.success(`Invitation resent to ${invitation.email}`)
    },
    onError: (error) => toast.error(describe(error)),
  })
  const cancel = useMutation({
    mutationFn: (id: string) => api<void>(`${base}/invitations/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await refresh()
      toast("Invitation cancelled")
    },
    onError: (error) => toast.error(describe(error)),
  })
  const changeRole = useMutation({
    mutationFn: (input: { id: string; role: Role }) => api<Member>(`${base}/members/${input.id}`, { method: "PATCH", body: JSON.stringify({ role: input.role }) }),
    onSuccess: async (member) => {
      await refresh()
      setRoleTarget(null)
      toast.success(`${member.name} is now ${ROLE_LABELS[member.role] === "Admin" ? "an" : "a"} ${ROLE_LABELS[member.role]}`)
    },
    onError: (error) => toast.error(describe(error)),
  })
  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`${base}/members/${id}`, { method: "DELETE" }),
    onSuccess: async (_, id) => {
      const removed = team.members.find((m) => m.id === id)
      await refresh()
      setRemoveTarget(null)
      toast(`${removed?.name ?? "Teammate"} removed from ${workspace.name}`)
    },
    onError: (error) => toast.error(describe(error)),
  })

  function submitInvite(event: FormEvent) {
    event.preventDefault()
    invite.mutate({ email: email.trim().toLowerCase(), role })
  }

  const rows: Row[] = [
    ...team.members.map((member) => ({ kind: "member" as const, key: `m-${member.id}`, member })),
    ...team.invitations.map((invitation) => ({ kind: "invitation" as const, key: `i-${invitation.id}`, invitation })),
  ]
  const filtered = rows.filter((row) => {
    const text = row.kind === "member" ? `${row.member.name} ${row.member.email}` : row.invitation.email
    const rowRole = row.kind === "member" ? row.member.role : row.invitation.role
    return text.toLowerCase().includes(search.toLowerCase()) && (filter === "all" || rowRole === filter)
  })

  return (
    <>
      <Heading
        title="Team members"
        description="A shared workspace. Clear responsibilities."
        action={
          canManage ? (
            <Button onClick={() => setOpen(true)}>
              <Plus />
              Invite teammate
            </Button>
          ) : undefined
        }
      />
      <div className="ws-toolbar">
        <div className="ws-search">
          <Search size={16} />
          <Input aria-label="Search team members" placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger aria-label="Filter by role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLE_FILTERS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/settings/roles" params={{ workspace: workspace.slug }}>
            View permissions
          </Link>
        </Button>
      </div>
      <DataTable
        heads={["Name", "Role", "Status", "Last active", ""]}
        rowKeys={filtered.map((row) => row.key)}
        rows={filtered.map((row) =>
          row.kind === "member"
            ? [
                <div className="ws-person">
                  <MiniAvatar name={row.member.name} />
                  <div>
                    <strong>
                      {row.member.name}
                      {row.member.userId === me.id ? <span className="text-muted-foreground font-normal"> · you</span> : null}
                    </strong>
                    <small>{row.member.email}</small>
                  </div>
                </div>,
                `${ROLE_LABELS[row.member.role]}${row.member.isOwner ? " · owner" : ""}`,
                <Badge variant="secondary">Active</Badge>,
                <span className="text-muted-foreground">{relativeTime(row.member.lastActiveAt)}</span>,
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" variant="ghost" aria-label={`Actions for ${row.member.name}`}>
                      <MoreHorizontal />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to="/w/$workspace/settings/roles" params={{ workspace: workspace.slug }}>
                        View role permissions
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        void navigator.clipboard?.writeText(row.member.email)
                        toast("Email address copied")
                      }}
                    >
                      Copy email address
                    </DropdownMenuItem>
                    {canManage && !row.member.isOwner && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            setNextRole(row.member.role)
                            setRoleTarget(row.member)
                          }}
                        >
                          Change role
                        </DropdownMenuItem>
                        <DropdownMenuItem variant="destructive" onClick={() => setRemoveTarget(row.member)}>
                          Remove from workspace
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>,
              ]
            : [
                <div className="ws-person">
                  <MiniAvatar name={row.invitation.email} />
                  <div>
                    <strong>{row.invitation.email.split("@")[0]}</strong>
                    <small>{row.invitation.email}</small>
                  </div>
                </div>,
                ROLE_LABELS[row.invitation.role],
                <Badge variant="outline">Invited</Badge>,
                <span className="text-muted-foreground" title={`Expires ${formatDateTime(row.invitation.expiresAt)}`}>
                  Invitation pending
                </span>,
                canManage ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" variant="ghost" aria-label={`Actions for ${row.invitation.email}`}>
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => resend.mutate(row.invitation.id)}>Resend invitation</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => cancel.mutate(row.invitation.id)}>Cancel invitation</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null,
              ]
        )}
      />
      {!filtered.length && <p className="ws-empty">No teammates match your search.</p>}
      <div className="ws-table-foot">
        <span>
          {team.members.length} active member{team.members.length === 1 ? "" : "s"} · {team.invitations.length} pending invitation{team.invitations.length === 1 ? "" : "s"}
        </span>
        <span>{workspace.name}</span>
      </div>
      <Hint title="Give teammates the access they need">Agents can reply, assign and resolve. Publishing and workspace settings are restricted by role.</Hint>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite a teammate</DialogTitle>
            <DialogDescription>Give someone a place in {workspace.name}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitInvite} className="ws-dialog-form">
            <Field label="Work email">{(id) => <Input id={id} type="email" required placeholder="teammate@example.com" value={email} autoFocus onChange={(e) => setEmail(e.target.value)} />}</Field>
            <Pick label="Role" value={role} options={ROLE_OPTIONS} onChange={setRole} />
            <p className="ws-hint">{ROLE_SUMMARY[role]}</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={invite.isPending}>
                Send invitation
                <ArrowRight />
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={roleTarget !== null} onOpenChange={(value) => !value && setRoleTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change role</DialogTitle>
            <DialogDescription>{roleTarget ? `${roleTarget.name} is currently ${ROLE_LABELS[roleTarget.role] === "Admin" ? "an" : "a"} ${ROLE_LABELS[roleTarget.role]}.` : ""}</DialogDescription>
          </DialogHeader>
          <div className="ws-dialog-form">
            <Pick label="New role" value={nextRole} options={ROLE_OPTIONS} onChange={setNextRole} />
            <p className="ws-hint">{ROLE_SUMMARY[nextRole]}</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRoleTarget(null)}>
                Cancel
              </Button>
              <Button type="button" disabled={!roleTarget || nextRole === roleTarget.role || changeRole.isPending} onClick={() => roleTarget && changeRole.mutate({ id: roleTarget.id, role: nextRole })}>
                Update role
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={removeTarget !== null} onOpenChange={(value) => !value && setRemoveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove {removeTarget?.name}?</DialogTitle>
            <DialogDescription>They lose access to {workspace.name} immediately. Conversations they handled stay in the inbox, and the change is recorded in the audit log.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRemoveTarget(null)}>
              Keep teammate
            </Button>
            <Button type="button" variant="destructive" disabled={remove.isPending} onClick={() => removeTarget && remove.mutate(removeTarget.id)}>
              Remove from workspace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
